/**
 * Appareils de Mon compte :
 * - « Appareils et sessions » (décision 16) : chaque navigateur ou téléphone
 *   connecté, « cet appareil » en tête ; on ferme une session que l'on ne
 *   reconnaît pas, ou l'on retire un téléphone perdu (ses données hors ligne
 *   sont effacées à sa prochaine connexion au réseau) ;
 * - appareils de confiance : ceux où le code de vérification n'est plus
 *   demandé à la connexion (montrés seulement s'il en existe), à oublier un
 *   par un ou tous.
 * « Déconnecter tous mes appareils » est une action rare : elle vit dans le
 * menu « … » de la page et se confirme (`useLogoutEverywhere`).
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Monitor, MonitorSmartphone, Smartphone, Trash2 } from 'lucide-react';
import { api } from '../../core/api.js';
import { forgetTrustedDevice, useAuth } from '../../core/auth.js';
import { dateFr, dateHeureFr } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { ConfirmActionModal } from '../../components/ui.js';
import { Badge } from '../../components/ui/Badge.js';
import { Banner } from '../../components/ui/Feedback.js';
import { Section } from '../../components/ui/Section.js';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';

interface TrustedDevice {
  id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
}

/** « Chrome sur Windows » plutôt qu'une chaîne technique de navigateur. */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Appareil inconnu';
  const browser = /Edg\//.test(userAgent) ? 'Edge'
    : /Firefox\//.test(userAgent) ? 'Firefox'
      : /Chrome\//.test(userAgent) ? 'Chrome'
        : /Safari\//.test(userAgent) ? 'Safari' : 'Navigateur';
  const system = /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iPhone ou iPad'
      : /Windows/.test(userAgent) ? 'Windows'
        : /Mac OS X/.test(userAgent) ? 'Mac'
          : /Linux/.test(userAgent) ? 'Linux' : undefined;
  return system ? `${browser} sur ${system}` : browser;
}

interface OpenSession {
  id: string;
  kind: 'web' | 'mobile';
  current: boolean;
  startedAt: string;
  lastActiveAt: string;
  userAgent: string | null;
  device: { id: string; name: string | null; platform: 'android' | 'ios' | 'web' | null } | null;
}

/** Nom lisible d'une session : l'appareil enregistré, sinon le navigateur. */
function sessionTitle(session: OpenSession): string {
  if (session.device?.name) return session.device.name;
  if (session.device?.platform === 'android') return t('compte.sessions.android');
  if (session.device?.platform === 'ios') return t('compte.sessions.ios');
  return describeDevice(session.userAgent);
}

/** Sessions ouvertes du compte : la liste, fermer une session, retirer un appareil perdu. */
export function SessionsSection(): ReactNode {
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: ['auth-sessions'],
    queryFn: () => api<{ items: OpenSession[]; limit: number }>('/auth/sessions'),
  });
  const [removing, setRemoving] = useState<OpenSession>();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['auth-sessions'] });
  const close = useMutation({
    mutationFn: (id: string) => api(`/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: (deviceId: string) => api(`/auth/devices/${deviceId}/revoke`, { method: 'POST' }),
    onSuccess: async () => {
      setRemoving(undefined);
      await refresh();
    },
  });
  const items = sessions.data?.items ?? [];
  return (
    <Section
      title={t('compte.sessions.titre')}
      hint={t('compte.sessions.aide')}
      collapsible
      id="sessions"
      flush
      {...(sessions.data ? { count: items.length } : {})}
    >
      {sessions.error ? <Banner tone="danger">{(sessions.error as Error).message}</Banner> : null}
      {close.error ? <Banner tone="danger">{(close.error as Error).message}</Banner> : null}
      {sessions.data ? (
        <RecordList label={t('compte.sessions.titre')}>
          {items.map((session) => (
            <li key={session.id}>
              <RecordRow
                lead={(
                  <RecordIcon>
                    {session.device || session.kind === 'mobile' ? <Smartphone size={20} /> : <Monitor size={20} />}
                  </RecordIcon>
                )}
                title={sessionTitle(session)}
                {...(session.current ? { badge: <Badge tone="ok">{t('compte.sessions.cetAppareil')}</Badge> } : {})}
                lines={[t('compte.sessions.dates', {
                  debut: dateFr(session.startedAt), activite: dateHeureFr(session.lastActiveAt),
                })]}
                {...(session.current ? {} : {
                  menuLabel: t('compte.sessions.actions', { appareil: sessionTitle(session) }),
                  menu: [
                    {
                      label: t('compte.sessions.fermer'),
                      icon: <LogOut size={18} aria-hidden="true" />,
                      onSelect: () => close.mutate(session.id),
                    },
                    ...(session.device ? [{
                      label: t('compte.sessions.retirerAppareil'),
                      icon: <Trash2 size={18} aria-hidden="true" />,
                      tone: 'danger' as const,
                      onSelect: () => { revoke.reset(); setRemoving(session); },
                    }] : []),
                  ],
                })}
              />
            </li>
          ))}
        </RecordList>
      ) : null}
      <ConfirmActionModal
        open={Boolean(removing)}
        title={t('compte.sessions.retirerTitre')}
        consequence={t('compte.sessions.retirerEffet', { appareil: removing ? sessionTitle(removing) : '' })}
        confirmLabel={t('compte.sessions.retirerConfirmer')}
        tone="danger"
        pending={revoke.isPending}
        error={revoke.error ? (revoke.error as Error).message : undefined}
        onClose={() => setRemoving(undefined)}
        onConfirm={() => {
          if (removing?.device && !revoke.isPending) revoke.mutate(removing.device.id);
        }}
      />
    </Section>
  );
}

/** Appareils de confiance : rien quand il n'y en a pas, une erreur dite quand la liste manque. */
export function TrustedDevicesSection(): ReactNode {
  const queryClient = useQueryClient();
  const devices = useQuery({
    queryKey: ['trusted-devices'],
    queryFn: () => api<{ items: TrustedDevice[]; days: number }>('/auth/trusted-devices'),
  });
  const items = devices.data?.items ?? [];
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['trusted-devices'] });
  const forgetOne = useMutation({
    mutationFn: (id: string) => api(`/auth/trusted-devices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      // Le serveur ne dit pas lequel est « cet appareil » : quand il n'en reste
      // aucun, le jeton local est forcément mort, on le retire.
      if (items.length <= 1) forgetTrustedDevice();
      return refresh();
    },
  });
  const forgetAll = useMutation({
    mutationFn: () => api('/auth/trusted-devices', { method: 'DELETE' }),
    onSuccess: () => {
      forgetTrustedDevice();
      return refresh();
    },
  });
  if (devices.error) {
    return <Banner tone="danger" title={t('compte.appareils.titre')}>{(devices.error as Error).message}</Banner>;
  }
  if (items.length === 0) return null;
  const error = forgetOne.error ?? forgetAll.error;
  return (
    <Section
      title={t('compte.appareils.titre')}
      count={items.length}
      hint={t('compte.appareils.aide')}
      flush
      {...(items.length > 1
        ? { action: { label: t('compte.appareils.oublierTous'), onClick: () => forgetAll.mutate(), pending: forgetAll.isPending } }
        : {})}
    >
      {error ? <Banner tone="danger">{(error as Error).message}</Banner> : null}
      <RecordList label={t('compte.appareils.titre')}>
        {items.map((device) => (
          <li key={device.id}>
            <RecordRow
              lead={<RecordIcon><MonitorSmartphone size={20} /></RecordIcon>}
              title={describeDevice(device.label)}
              lines={[t('compte.appareils.dates', { ajout: dateFr(device.created_at), fin: dateFr(device.expires_at) })]}
              menu={[{
                label: t('compte.appareils.oublier'),
                icon: <Trash2 size={18} aria-hidden="true" />,
                tone: 'danger',
                onSelect: () => forgetOne.mutate(device.id),
              }]}
            />
          </li>
        ))}
      </RecordList>
    </Section>
  );
}

/** « Déconnecter tous mes appareils » : confirmé, puis retour à la connexion. */
export function useLogoutEverywhere(): { open: () => void; dialog: ReactNode } {
  const { logout } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const run = useMutation({
    mutationFn: () => api('/auth/logout-all', { method: 'POST' }),
    // Cet appareil fait partie de « tous » : retour à l'écran de connexion.
    onSuccess: () => logout(),
  });
  return {
    open: () => {
      run.reset();
      setConfirming(true);
    },
    dialog: (
      <ConfirmActionModal
        open={confirming}
        title={t('compte.deconnecterTout.titre')}
        consequence={t('compte.deconnecterTout.consequence')}
        confirmLabel={t('compte.deconnecterTout.confirmer')}
        tone="danger"
        pending={run.isPending}
        error={run.error ? (run.error as Error).message : undefined}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          if (!run.isPending) run.mutate();
        }}
      />
    ),
  };
}
